import * as React from 'react';

import StyledModal from '../Components/StyledModal';
import { CalendarEventForm } from '../Components/Calendar/CalendarEventForm';
import { CalendarEvent } from '../../MedicareProAPI/Entities/CalendarEvent';
import type { CalendarEventSchema } from '../../MedicareProAPI/Repositories/CalendarEventRepository';
import { CalendarEventRepository } from '../../MedicareProAPI/Repositories/CalendarEventRepository';
import { UserContext } from '../Context/UserContext';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { CalendarService } from '../Panels/AgentEventCalendarPanel';
import { CALENDAR_TYPE } from '../Panels/AgentEventCalendarPanel';
import { UserIntegrationProviderRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationProviderRepository';
import { ProviderCalendarEvent } from '../../MedicareProAPI/Entities/External/ProviderCalendarEvent';
import { UserIntegrationOAuthRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthRepository';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import Loader from '../Components/Loader';
import type { Operator } from '../../MedicareProAPI/Model/Operator';

export interface CalendarCreateEventButtonProps {
  indId: number;
  operator?: Operator;
  element?: React.JSX.Element;
  directInstantiation?: boolean;
  onEventCreate?: () => void;
  onClose?: () => void;
}

export const CalendarCreateEventButton = (
  props: CalendarCreateEventButtonProps
): React.JSX.Element => {
  const [show, setShow] = React.useState(
    props?.directInstantiation ? props.directInstantiation === true : false
  );
  const [calendarEvent, setCalendarEvent] = React.useState<CalendarEvent>();
  const [calendarServices, setCalendarServices] = React.useState<CalendarService[]>();
  const [calendarType, setCalendarType] = React.useState<CALENDAR_TYPE>(
    (localStorage.getItem('calendar-type') as CALENDAR_TYPE) || CALENDAR_TYPE.DEFAULT
  );

  // Bind event handler to whatever element will be used as the button
  const defaultElement = (
    <span>
      <i className="fas fa-calendar mr-2 text-primary"></i>Set Appointment
    </span>
  );
  const element: React.JSX.Element = React.cloneElement(props.element || defaultElement, {
    onClick: (e) => handleNewEventStart(),
  });

  const individual = React.useRef<Ind>(null);

  const userData = React.useContext(UserContext);

  const userIntegrationProviderRepository = new UserIntegrationProviderRepository();
  userIntegrationProviderRepository.setAccessToken(userData.userAccessToken);

  const userIntegrationOAuthRepository = new UserIntegrationOAuthRepository();
  userIntegrationOAuthRepository.setAccessToken(userData.userAccessToken);

  const calendarEventRepository = new CalendarEventRepository();
  calendarEventRepository.setAccessToken(userData.userAccessToken);

  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(userData.userAccessToken);

  React.useEffect(() => {
    if (calendarEvent) {
      handleNewEventStart();
    }

    if (calendarType) {
      localStorage.setItem('calendar-type', calendarType);
    }

    if (props?.directInstantiation) {
      handleNewEventStart();
    }
  }, [calendarType]);

  /**
   * Start creating a new event
   */
  const handleNewEventStart = () => {
    setShow(true);

    // If calendar services haven't loaded then pull them
    if (!calendarServices) {
      const newCalendarServices: CalendarService[] = [
        {
          name: 'Default',
          type: CALENDAR_TYPE.DEFAULT,
        },
      ];

      userIntegrationProviderRepository
        .getUserIntegrationFeatureAvailability(userData.userId, 'calendar')
        .then((integrations) => {
          integrations.getItems().forEach((integration) => {
            newCalendarServices.push({
              name: integration.getName(),
              type: integration.getReference() as CALENDAR_TYPE,
            });
          });

          setCalendarServices(newCalendarServices);

          // Select the user's integrated calendar if they have one connected
          // TODO: Could perhaps be more elegant
          if (localStorage.getItem('calendar-type')) {
            const newClaendarType = localStorage.getItem('calendar-type') as CALENDAR_TYPE;
            setCalendarType(newClaendarType);
            createNewEvent(newClaendarType);
          } else if (
            newCalendarServices &&
            newCalendarServices.length > 1 &&
            newCalendarServices[newCalendarServices.length - 1]
          ) {
            const newCalendarType = newCalendarServices[newCalendarServices.length - 1].type;
            setCalendarType(newCalendarType);
            createNewEvent(newCalendarType);
          } else {
            createNewEvent(calendarType);
          }
        });
    } else {
      createNewEvent(calendarType);
    }
  };

  /**
   * Create new event
   */
  const createNewEvent = (calendarType: CALENDAR_TYPE) => {
    // Create the new event
    let newEvent;

    // Event is an internal event
    if (calendarType === CALENDAR_TYPE.DEFAULT) {
      newEvent = new CalendarEvent();
    }
    // Event is an external event that doesn't exist in our system
    else {
      newEvent = new ProviderCalendarEvent();
    }

    if (props.operator) {
      newEvent.setCreator(props.operator);
    }

    if (props.indId) {
      console.log('indId for calendar: ', props.indId);
      individualRepository
        .getInd(props.indId, { include: ['addresses', 'phones', 'spouse', 'spouse.addresses'] })
        .then((ind) => {
          individual.current = ind;
          newEvent.addInd(ind);
          setCalendarEvent(newEvent);
        });
    } else {
      setCalendarEvent(newEvent);
    }
  };

  /**
   * Saves changes made to the CalendarEvent
   *
   * @param {CalendarEvent} calendarEvent Calendar even that is being saved or created
   * @returns {Promise<CalendarEvent>} Promise containing the updated CalendarEvent object
   */
  const formOnSave = (calendarEvent: CalendarEvent): Promise<CalendarEvent> => {
    let calendarEventResponse;

    // Microsoft
    if (
      calendarType === CALENDAR_TYPE.MICROSOFT &&
      calendarEvent instanceof ProviderCalendarEvent
    ) {
      if (calendarEvent.isAllDayEvent()) {
        calendarEvent.getDateTimeStart().setUTCHours(0);
        calendarEvent.getDateTimeStart().setUTCMinutes(0);
        calendarEvent.getDateTimeStart().setUTCSeconds(0);

        const dateEnd = new Date(calendarEvent.getDateTimeStart());
        dateEnd.setDate(dateEnd.getDate() + 1);

        calendarEvent.setDateTimeEnd(dateEnd);
      }

      const calendarEventPayload: any = {
        subject: calendarEvent.getName(),
        start: {
          dateTime: calendarEvent.getDateTimeStart().toJSON().slice(0, 19),
          timeZone: 'UTC',
        },
        end: {
          dateTime: calendarEvent.getDateTimeEnd().toJSON().slice(0, 19),
          timeZone: 'UTC',
        },
        isAllDay: calendarEvent.isAllDayEvent(),
      };

      if (!calendarEvent.getExternalID()) {
        calendarEventPayload.body = {
          content: calendarEvent.getDescription(),
          contentType: 'html',
        };

        calendarEventPayload.location = {
          displayName: calendarEvent.getLocation(),
          locationType: 'default',
        };
      }

      // Update existing event
      if (calendarEvent.getExternalID()) {
        calendarEventResponse =
          userIntegrationOAuthRepository.updateUserIntegrationOAuthCalendarEvent(
            userData.userId,
            calendarType,
            calendarEvent.getExternalID(),
            calendarEventPayload
          );
      }
      // Create new Event
      else {
        calendarEventResponse =
          userIntegrationOAuthRepository.createUserIntegrationOAuthCalendarEvent(
            userData.userId,
            calendarType,
            calendarEventPayload
          );
      }

      // TODO: This part doesn't feel good
      calendarEventResponse.then((updatedEvent) => {
        // If an Ind is set, add a reference to this ind
        if (calendarEvent.getInds()) {
          calendarEventRepository.putCalendarEventExternal(
            userData.userId,
            updatedEvent.getExternalID(),
            {
              inds: calendarEvent.getInds(),
            }
          );
        }
        // Otherwise attempt to delete this record
        else {
          calendarEventRepository.deleteCalendarEventExternal(
            userData.userId,
            updatedEvent.getExternalID()
          );
        }

        updatedEvent.setInds(calendarEvent.getInds());

        if (props?.onEventCreate) {
          props.onEventCreate();
        }

        return updatedEvent;
      });
    }
    // Default
    else {
      const calendarEventPayload: CalendarEventSchema = {
        name: calendarEvent.getName(),
        inds: calendarEvent.getInds(),
        description: calendarEvent.getDescription(),
        location: calendarEvent.getLocation(),
        dateTimeStart: calendarEvent.getDateTimeStart(),
        dateTimeEnd: calendarEvent.getDateTimeEnd(),
        isAllDayEvent: calendarEvent.isAllDayEvent(),
        creator: calendarEvent.getCreator(),
      };

      if (calendarEvent.getId()) {
        calendarEventResponse = calendarEventRepository.updateCalendarEvent(
          calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : userData.userId,
          calendarEvent.getId(),
          calendarEventPayload
        );
      } else {
        calendarEventResponse = calendarEventRepository.createCalendarEvent(
          calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : userData.userId,
          calendarEventPayload
        );
      }
    }

    calendarEventResponse.then((calendarEvent) => {
      setShow(false);
      if (props?.onEventCreate) {
        props.onEventCreate();
      }
    });

    return calendarEventResponse;
  };

  function handleClose() {
    setShow(false);
    props.onClose ? props.onClose() : '';
  }

  return (
    <>
      {!props?.directInstantiation && element}

      <StyledModal
        size="md"
        title="Create Event"
        footerCloseButton={true}
        show={show}
        onClose={(e) => handleClose()}
      >
        {calendarEvent && (
          <>
            <div className="row">
              {calendarServices && calendarServices.length > 1 && (
                <div className="col-12 pt-1 pb-2">
                  <div className="small float-left pt-1 pr-2">Select Calendar Service</div>
                  <select
                    className="form-control form-control-sm w-auto"
                    onChange={(e) => {
                      const newCalendarType = e.target.value as CALENDAR_TYPE;
                      setCalendarType(newCalendarType);
                    }}
                  >
                    {calendarServices.map((integration) => (
                      <option
                        selected={integration.type === calendarType}
                        value={integration.type}
                        key={integration.type}
                      >
                        {integration.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <CalendarEventForm onSave={formOnSave} calendarEvent={calendarEvent} />
          </>
        )}
        {!calendarEvent && <Loader size="large" className="mt-4 mb-4" />}
      </StyledModal>
    </>
  );
};
